import React, { useState } from "react";
import { useCustomerForm } from "../hooks/useCustomerForm";
import { removeEmoji } from "../../../../../utils/removeEmoji";
import { formatPincode } from "../../../../../utils/formatPincode";
import CommonButtons from "../../../../../../shared/components/CommonButtons";
import { ModalWrapper } from "../../../../../../shared/components/ModalWrapper";
import { CustomerData } from "src/shared/services/interfacev2";
import InputGroup from "src/business-types/grocery/layouts/setting/customer/InputGroup";

interface Props {
  onBack: () => void;
  onAdded: (customer: CustomerData) => void;
}

const CustomerForm: React.FC<Props> = ({ onBack, onAdded }) => {
  const { formData, emailError, errorEmailExists, submitting, handleInputChange, handleAddCustomer, validateEmail } = useCustomerForm({
    onSuccess: onAdded,
    onBack,
  });

  const [showMore, setShowMore] = useState(false);

  // ✅ Enable Add button only when required fields are valid
  const isFormValid = formData.firstName.trim() !== "" && formData.phone.trim().length === 8 && !emailError && !errorEmailExists;
  const pincodeLength = formData.address4.trim().length;

  const isValidPincode = pincodeLength === 0 || (pincodeLength >= 3 && pincodeLength <= 10);

  return (
    <ModalWrapper title="Enter Customer Details" onClose={onBack} showBackButton onBack={onBack} width="max-w-2xl" className="min-w-[560px]" positionClass="top-0 left-0 right-0 mt-16 h-full">
      <div className="grid grid-cols-2 gap-4">
        {/* ================= REQUIRED DETAILS ================= */}
        <InputGroup label="Name" type="text" name="firstName" value={formData.firstName} mandatory onChange={(name, value) => handleInputChange(name as any, removeEmoji(value).slice(0, 25))} />

        <InputGroup label="Phone Number" type="number" name="phone" value={formData.phone} mandatory onChange={(name, value) => handleInputChange(name as any, value.replace(/\D/g, "").slice(0, 8))} />

        {/* ================= + MORE DETAILS TOGGLE ================= */}
        <div className="col-span-2 flex justify-end">
          <p className="cursor-pointer text-blue-600 hover:underline text-sm" onClick={() => setShowMore(!showMore)}>
            {showMore ? "Hide Details" : "+ More Details"}
          </p>
        </div>

        {/* ================= OPTIONAL DETAILS ================= */}
        {showMore && (
          <>
            <InputGroup label="Middle Name" type="text" name="middleName" value={formData.middleName} onChange={(name, value) => handleInputChange(name as any, removeEmoji(value).slice(0, 25))} />

            <InputGroup label="Last Name" type="text" name="lastName" value={formData.lastName} onChange={(name, value) => handleInputChange(name as any, removeEmoji(value).slice(0, 25))} />

            <InputGroup
              label="Alternative Phone"
              type="number"
              name="alternativePhone"
              value={formData.alternativePhone}
              onChange={(name, value) => handleInputChange(name as any, value.replace(/\D/g, "").slice(0, 8))}
            />

            {/* ================= EMAIL ================= */}
            <div className="flex flex-col gap-1">
              <InputGroup
                label="Email (Optional)"
                type="email"
                name="email"
                value={formData.email}
                onChange={(name, value) => {
                  const clean = removeEmoji(value);
                  handleInputChange(name as any, clean);

                  if (errorEmailExists) {
                    validateEmail(clean, true);
                  } else {
                    validateEmail(clean);
                  }
                }}
              />

              {(emailError || errorEmailExists) && <span className="text-xs text-red-600">{errorEmailExists ? "Email already exists" : "Invalid email format"}</span>}
            </div>

            {/* ================= ADDRESS ================= */}
            <div className="col-span-2 mt-2">
              <label className="block text-lg font-semibold mb-2">Address (Optional)</label>

              <div className="border border-gray-300 rounded-lg p-3 grid grid-cols-2 gap-4">
                <InputGroup label="Street / Village" type="text" name="address" value={formData.address} onChange={(name, value) => handleInputChange(name as any, removeEmoji(value))} />

                <InputGroup label="District / State" type="text" name="address2" value={formData.address2} onChange={(name, value) => handleInputChange(name as any, removeEmoji(value))} />

                <InputGroup label="City" type="text" name="address3" value={formData.address3} onChange={(name, value) => handleInputChange(name as any, removeEmoji(value))} />
                <InputGroup
                  label="Pincode / Zipcode"
                  type="tel"
                  name="address4"
                  value={formData.address4}
                  onChange={(name, value) => {
                    const cleanedValue = value.replace(/\D/g, "").slice(0, 10);
                    handleInputChange(name as any, cleanedValue);
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* ================= ACTIONS ================= */}
        <div className="mt-4 flex justify-end gap-4 col-span-2">
          <CommonButtons className="w-36" label="Cancel" variant="outlineDanger" onClick={onBack} disabled={submitting} />

          <CommonButtons className="w-36" label="Add Customer" variant="primary" onClick={handleAddCustomer} loading={submitting} disabled={!isFormValid || submitting} />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CustomerForm;
